/**
 * Centralized API client for ARA Wealth
 * - Handles authentication automatically via cookies
 * - Automatic token refresh when access token expires
 * - Graceful token expiry handling with redirect + toast
 * - Single source of truth for all API calls
 * 
 * IMPORTANT: Always use relative paths for API calls to ensure cookies work correctly.
 * All API endpoints are prefixed with '/api' and routed by the ingress.
 */

import { toast } from "sonner";

// Always use empty string for same-origin requests
// This ensures cookies are sent correctly across all environments
const API_URL = '';

// Export for pages that need direct access
export const getApiUrl = () => API_URL;

// Track if we're currently refreshing to prevent multiple refresh calls
let isRefreshing = false;
let refreshPromise = null;

// Event emitter for auth state changes
const authEvents = {
  listeners: [],
  emit(event) {
    this.listeners.forEach(fn => fn(event));
  },
  subscribe(fn) {
    this.listeners.push(fn);
    return () => {
      this.listeners = this.listeners.filter(l => l !== fn);
    };
  }
};

export { authEvents };

/**
 * Attempt to refresh the access token using the refresh token
 * @returns {Promise<boolean>} - True if refresh succeeded
 */
async function refreshAccessToken() {
  // If already refreshing, wait for that to complete
  if (isRefreshing) {
    return refreshPromise;
  }
  
  isRefreshing = true;
  refreshPromise = (async () => {
    try {
      const response = await fetch(`${API_URL}/api/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        console.log('Token refreshed successfully');
        return true;
      }
      
      // Refresh failed - token revoked or expired
      console.log('Token refresh failed:', response.status);
      return false;
    } catch (error) {
      console.error('Token refresh error:', error);
      return false;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();
  
  return refreshPromise;
}

/**
 * Parse response body EXACTLY ONCE
 * - Reads body as JSON if Content-Type is application/json
 * - Falls back to text otherwise
 * - Throws with proper error message for non-ok responses
 * 
 * @param {Response} res - Fetch Response object
 * @returns {Promise<any>} - Parsed response payload
 * @throws {Error} - Error with message from response
 */
export async function parseResponse(res) {
  const contentType = res.headers.get("content-type") || "";
  const isJson = contentType.includes("application/json");
  let payload = null;

  try {
    payload = isJson ? await res.json() : await res.text();
  } catch (e) {
    // Body might be empty or malformed
    payload = null;
  }

  if (!res.ok) {
    const message =
      (payload && (payload.detail || payload.message || payload.error)) ||
      (typeof payload === "string" && payload) ||
      `Request failed (${res.status})`;
    
    // Create error with additional context
    const error = new Error(message);
    error.status = res.status;
    error.payload = payload;
    throw error;
  }

  return payload;
}

/**
 * Make an authenticated API request with automatic token refresh
 * @param {string} endpoint - API endpoint (e.g., '/api/auth/me')
 * @param {object} options - Fetch options
 * @param {boolean} isRetry - Whether this is a retry after token refresh
 * @returns {Promise<Response>}
 */
async function request(endpoint, options = {}, isRetry = false) {
  const url = endpoint.startsWith('http') ? endpoint : `${API_URL}${endpoint}`;
  
  const config = {
    credentials: 'include', // Always include cookies
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  };
  
  // Remove Content-Type for FormData
  if (options.body instanceof FormData) {
    delete config.headers['Content-Type'];
  }
  
  try {
    const response = await fetch(url, config);
    
    // Handle 401 with automatic token refresh
    if (response.status === 401 && !isRetry) {
      // Don't try to refresh for auth endpoints
      const isAuthEndpoint = endpoint.includes('/auth/login') || 
                             endpoint.includes('/auth/register') ||
                             endpoint.includes('/auth/refresh');
      
      if (!isAuthEndpoint) {
        console.log('Access token expired, attempting refresh...');
        const refreshed = await refreshAccessToken();
        
        if (refreshed) {
          // Retry the original request
          return request(endpoint, options, true);
        }
      }
      
      // Hydration/probe endpoints (checking whether user is logged in) —
      // never surface a "session expired" toast for anonymous visitors.
      const isHydrationEndpoint = endpoint.includes('/auth/me') ||
                                  endpoint.includes('/auth/check') ||
                                  endpoint.includes('/auth/refresh');

      // Public marketing / legal routes — a first-time visitor must never
      // see an auth error toast for pages that don't require auth.
      const publicPaths = ['/', '/how-it-works', '/pricing', '/about', '/contact',
                           '/join-beta', '/legacy-landing', '/privacy', '/terms',
                           '/security', '/security/open-banking'];
      const isPublicRoute = publicPaths.includes(window.location.pathname);

      // Refresh failed or not applicable - emit auth error only for
      // previously-authenticated sessions on protected routes.
      if (!isHydrationEndpoint && !isPublicRoute) {
        authEvents.emit({ type: 'AUTH_ERROR', message: 'Session expired' });
      }

      const isAuthPage = window.location.pathname.includes('/login') ||
                         window.location.pathname.includes('/register');
      const isEmailActionPage = window.location.pathname.startsWith('/invite/') ||
                                window.location.pathname.startsWith('/action/');

      if (!isAuthPage && !isEmailActionPage && !isHydrationEndpoint && !isPublicRoute) {
        toast.error('Session expired. Please log in again.');
      }
    }
    
    return response;
  } catch (error) {
    console.error('API request failed:', error);
    // Suppress network-error toast on hydration probes and public routes so
    // anonymous marketing visitors never see red error toasts.
    const isHydrationEndpoint = endpoint.includes('/auth/me') ||
                                endpoint.includes('/auth/check') ||
                                endpoint.includes('/auth/refresh');
    const publicPaths = ['/', '/how-it-works', '/pricing', '/about', '/contact',
                         '/join-beta', '/legacy-landing', '/privacy', '/terms',
                         '/security', '/security/open-banking'];
    const isPublicRoute = publicPaths.includes(window.location.pathname);
    if (!isHydrationEndpoint && !isPublicRoute) {
      toast.error('Network error. Please check your connection.');
    }
    throw error;
  }
}

/**
 * API methods
 */
const api = {
  /**
   * GET request
   */
  async get(endpoint, options = {}) {
    return request(endpoint, { method: 'GET', ...options });
  },
  
  /**
   * POST request
   */
  async post(endpoint, data, options = {}) {
    const body = data instanceof FormData ? data : JSON.stringify(data);
    return request(endpoint, { method: 'POST', body, ...options });
  },
  
  /**
   * PUT request
   */
  async put(endpoint, data, options = {}) {
    return request(endpoint, { method: 'PUT', body: JSON.stringify(data), ...options });
  },
  
  /**
   * DELETE request
   */
  async delete(endpoint, options = {}) {
    return request(endpoint, { method: 'DELETE', ...options });
  },
  
  /**
   * PATCH request
   */
  async patch(endpoint, data, options = {}) {
    return request(endpoint, { method: 'PATCH', body: JSON.stringify(data), ...options });
  },
  
  // ============ Auth API ============
  auth: {
    async me() {
      return api.get('/api/auth/me');
    },
    
    async check() {
      return api.get('/api/auth/check');
    },
    
    async login(email, password) {
      return api.post('/api/auth/login', { email, password });
    },
    
    async register(name, email, password) {
      return api.post('/api/auth/register', { name, email, password });
    },
    
    async logout() {
      return api.post('/api/auth/logout', {});
    },
    
    async demo() {
      return api.post('/api/auth/demo', {});
    },
    
    async exchangeSession(sessionId) {
      return api.post('/api/auth/session', { session_token: sessionId });
    }
  },
  
  // ============ Household API ============
  households: {
    async list() {
      return api.get('/api/households');
    },
    
    async get(householdId) {
      return api.get(`/api/households/${householdId}`);
    },
    
    async create(data) {
      return api.post('/api/households', data);
    },
    
    async update(householdId, data) {
      return api.put(`/api/households/${householdId}`, data);
    },
    
    async invite(householdId, email, role) {
      return api.post(`/api/households/${householdId}/invite`, { email, role });
    },
    
    async members(householdId) {
      return api.get(`/api/households/${householdId}/members`);
    }
  },
  
  // ============ Transactions API ============
  transactions: {
    async list(householdId, params = {}) {
      const query = new URLSearchParams(params).toString();
      return api.get(`/api/households/${householdId}/transactions${query ? `?${query}` : ''}`);
    },
    
    async create(householdId, data) {
      return api.post(`/api/households/${householdId}/transactions`, data);
    },
    
    async update(householdId, txnId, data) {
      return api.put(`/api/households/${householdId}/transactions/${txnId}`, data);
    },
    
    async delete(householdId, txnId) {
      return api.delete(`/api/households/${householdId}/transactions/${txnId}`);
    },
    
    async bulkUpdate(householdId, updates) {
      return api.post(`/api/households/${householdId}/transactions/bulk-update`, updates);
    },
    
    async import(householdId, formData) {
      return api.post(`/api/households/${householdId}/transactions/import`, formData);
    }
  },
  
  // ============ Transfers API ============
  transfers: {
    async detected(householdId) {
      return api.get(`/api/households/${householdId}/transactions/transfers/detected`);
    },
    
    async confirmed(householdId) {
      return api.get(`/api/households/${householdId}/transactions/transfers/confirmed`);
    },
    
    async confirm(householdId, fromId, toId) {
      return api.post(`/api/households/${householdId}/transactions/transfers/confirm`, {
        from_transaction_id: fromId,
        to_transaction_id: toId
      });
    },
    
    async dismiss(householdId, transactionIds) {
      return api.post(`/api/households/${householdId}/transactions/transfers/dismiss`, {
        transaction_ids: transactionIds
      });
    },
    
    async unlink(householdId, groupId) {
      return api.post(`/api/households/${householdId}/transactions/transfers/unlink`, {
        transfer_group_id: groupId
      });
    }
  },
  
  // ============ Accounts API ============
  accounts: {
    async list(householdId) {
      return api.get(`/api/households/${householdId}/accounts`);
    },
    
    async create(householdId, data) {
      return api.post(`/api/households/${householdId}/accounts`, data);
    },
    
    async update(householdId, accountId, data) {
      return api.put(`/api/households/${householdId}/accounts/${accountId}`, data);
    },
    
    async delete(householdId, accountId) {
      return api.delete(`/api/households/${householdId}/accounts/${accountId}`);
    }
  },
  
  // ============ Connections API ============
  connections: {
    async list() {
      return api.get('/api/connections');
    },
    
    async create(data) {
      return api.post('/api/connections', data);
    },
    
    async get(connectionId) {
      return api.get(`/api/connections/${connectionId}`);
    },
    
    async update(connectionId, data) {
      return api.patch(`/api/connections/${connectionId}`, data);
    },
    
    async disconnect(connectionId, deleteAccounts = false) {
      return api.delete(`/api/connections/${connectionId}?delete_accounts=${deleteAccounts}`);
    },
    
    async migrateLegacy() {
      return api.post('/api/connections/migrate-legacy', {});
    },
    
    async ensureManual() {
      return api.post('/api/connections/manual', {});
    },
    
    async listAccounts(connectionId) {
      return api.get(`/api/connections/${connectionId}/accounts`);
    },
    
    async createAccount(connectionId, data) {
      return api.post(`/api/connections/${connectionId}/accounts`, data);
    }
  },
  
  // ============ Income API ============
  income: {
    async getSources(householdId) {
      return api.get(`/api/households/${householdId}/income/sources`);
    },
    
    async createSource(householdId, data) {
      return api.post(`/api/households/${householdId}/income/sources`, data);
    },
    
    async getSource(householdId, sourceId) {
      return api.get(`/api/households/${householdId}/income/sources/${sourceId}`);
    },
    
    async updateSource(householdId, sourceId, data) {
      return api.patch(`/api/households/${householdId}/income/sources/${sourceId}`, data);
    },
    
    async deleteSource(householdId, sourceId, deleteEvents = false) {
      return api.delete(`/api/households/${householdId}/income/sources/${sourceId}?delete_events=${deleteEvents}`);
    },
    
    async getEvents(householdId, params = {}) {
      const query = new URLSearchParams(params).toString();
      return api.get(`/api/households/${householdId}/income/events${query ? `?${query}` : ''}`);
    },
    
    async markReceived(householdId, eventId, actualAmount, actualDate) {
      return api.post(`/api/households/${householdId}/income/events/${eventId}/mark-received?actual_amount=${actualAmount}&actual_date=${actualDate}`, {});
    },
    
    async getDashboard(householdId) {
      return api.get(`/api/households/${householdId}/income/dashboard`);
    },
    
    async getWeeklyCheck(householdId, weekStart) {
      return api.get(`/api/households/${householdId}/income/weekly-check${weekStart ? `?week_start=${weekStart}` : ''}`);
    }
  },
  
  // ============ Reports API ============
  reports: {
    async dashboard(householdId) {
      return api.get(`/api/households/${householdId}/reports/dashboard`);
    },
    
    async spending(householdId, params = {}) {
      const query = new URLSearchParams(params).toString();
      return api.get(`/api/households/${householdId}/reports/spending${query ? `?${query}` : ''}`);
    },
    
    async incomeExpenses(householdId, params = {}) {
      const query = new URLSearchParams(params).toString();
      return api.get(`/api/households/${householdId}/reports/income-expenses${query ? `?${query}` : ''}`);
    },
    
    async netWorth(householdId) {
      return api.get(`/api/households/${householdId}/reports/net-worth`);
    }
  },
  
  // ============ Bills API ============
  bills: {
    async list(householdId) {
      return api.get(`/api/households/${householdId}/bills`);
    },
    
    async create(householdId, data) {
      return api.post(`/api/households/${householdId}/bills`, data);
    },
    
    async update(householdId, billId, data) {
      return api.put(`/api/households/${householdId}/bills/${billId}`, data);
    },
    
    async delete(householdId, billId) {
      return api.delete(`/api/households/${householdId}/bills/${billId}`);
    },
    
    async upcoming(householdId, days = 30) {
      return api.get(`/api/households/${householdId}/bills/upcoming/${days}`);
    },
    
    async markPaid(householdId, billId) {
      return api.post(`/api/households/${householdId}/bills/${billId}/mark-paid`, {});
    }
  },
  
  // ============ Goals API ============
  goals: {
    async annual(householdId) {
      return api.get(`/api/households/${householdId}/goals/annual`);
    },
    
    async createAnnual(householdId, data) {
      return api.post(`/api/households/${householdId}/goals/annual`, data);
    },
    
    async quarterly(householdId) {
      return api.get(`/api/households/${householdId}/plans/quarterly`);
    },
    
    async createQuarterly(householdId, data) {
      return api.post(`/api/households/${householdId}/plans/quarterly`, data);
    },
    
    async weekly(householdId) {
      return api.get(`/api/households/${householdId}/quests/weekly`);
    },
    
    async createWeekly(householdId, data) {
      return api.post(`/api/households/${householdId}/quests/weekly`, data);
    },
    
    async checkMilestones(householdId) {
      return api.post(`/api/households/${householdId}/milestones/check`, {});
    }
  },
  
  // ============ Reconciliation API ============
  reconcile: {
    async list(householdId) {
      return api.get(`/api/households/${householdId}/reconcile`);
    },
    
    async create(householdId, data) {
      return api.post(`/api/households/${householdId}/reconcile`, data);
    },
    
    async complete(householdId, sessionId) {
      return api.post(`/api/households/${householdId}/reconcile/${sessionId}/complete`, {});
    }
  },
  
  // ============ Rules API ============
  rules: {
    async list(householdId) {
      return api.get(`/api/households/${householdId}/rules`);
    },
    
    async create(householdId, data) {
      return api.post(`/api/households/${householdId}/rules`, data);
    },
    
    async update(householdId, ruleId, data) {
      return api.put(`/api/households/${householdId}/rules/${ruleId}`, data);
    },
    
    async delete(householdId, ruleId) {
      return api.delete(`/api/households/${householdId}/rules/${ruleId}`);
    },
    
    async reorder(householdId, ruleIds) {
      return api.post(`/api/households/${householdId}/rules/reorder`, { rule_ids: ruleIds });
    },
    
    async rerun(householdId) {
      return api.post(`/api/households/${householdId}/rules/rerun`, {});
    }
  },
  
  // ============ Mentor API ============
  mentor: {
    async chat(householdId, messages, mode) {
      return api.post(`/api/households/${householdId}/mentor/chat`, { messages, mode });
    },
    
    async simulateDebt(householdId, data) {
      return api.post(`/api/households/${householdId}/mentor/simulate/debt-payoff`, data);
    },
    
    async simulateGrowth(householdId, data) {
      return api.post(`/api/households/${householdId}/mentor/simulate/compound-growth`, data);
    }
  },
  
  // ============ Categories API ============
  categories: {
    async list(householdId) {
      return api.get(`/api/households/${householdId}/categories`);
    },
    
    async create(householdId, data) {
      return api.post(`/api/households/${householdId}/categories`, data);
    }
  },
  
  // ============ Review Queue API ============
  review: {
    async list(householdId) {
      return api.get(`/api/households/${householdId}/review`);
    }
  },
  
  // ============ Imports API ============
  imports: {
    async upload(householdId, formData) {
      return api.post(`/api/households/${householdId}/imports/csv`, formData);
    },
    
    async history(householdId) {
      return api.get(`/api/households/${householdId}/imports/history`);
    }
  },
  
  // ============ Admin API ============
  admin: {
    async getVisionPanel() {
      return api.get('/api/admin/vision-panel');
    },
    
    async getMetricsDashboard(days = 30) {
      return api.get(`/api/admin/metrics/dashboard?days=${days}`);
    },
    
    async getMetricTrend(metric, days = 30) {
      return api.get(`/api/admin/metrics/trend/${metric}?days=${days}`);
    }
  },
  
  // ============ Beta API ============
  beta: {
    // Invite codes (admin)
    async createInviteCode(data) {
      return api.post('/api/beta/invite-codes', data);
    },
    
    async listInviteCodes(includeInactive = false) {
      return api.get(`/api/beta/invite-codes?include_inactive=${includeInactive}`);
    },
    
    async deactivateInviteCode(codeId) {
      return api.put(`/api/beta/invite-codes/${codeId}/deactivate`, {});
    },
    
    async validateInviteCode(code) {
      return api.post('/api/beta/validate-code', { code });
    },

    async redeemCode(code) {
      return api.post('/api/beta/redeem-code', { code });
    },

    async createBatchCodes(data) {
      return api.post('/api/beta/invite-codes/batch', data);
    },
    
    // Feedback
    async submitFeedback(data) {
      return api.post('/api/beta/feedback', data);
    },
    
    async listFeedback(category = null, status = null, betaOnly = true) {
      const params = new URLSearchParams();
      if (category) params.append('category', category);
      if (status) params.append('status', status);
      params.append('beta_only', betaOnly);
      return api.get(`/api/beta/feedback?${params.toString()}`);
    },
    
    async updateFeedbackStatus(feedbackId, responseStatus, adminNotes = null) {
      return api.put(`/api/beta/feedback/${feedbackId}/status`, { 
        response_status: responseStatus,
        admin_notes: adminNotes
      });
    },
    
    // Metrics tracking
    async trackMetric(eventType, eventData = {}) {
      return api.post('/api/beta/metrics/track', { event_type: eventType, event_data: eventData });
    },
    
    // Dashboard (admin)
    async getDashboard(days = 30) {
      return api.get(`/api/beta/dashboard?days=${days}`);
    },
    
    // Beta households (admin)
    async listBetaHouseholds(source = null) {
      const params = source ? `?source=${source}` : '';
      return api.get(`/api/beta/households${params}`);
    }
  },
  
  // ============ Budget Categories (The Study) ============
  budgetCategories: {
    async list(householdId) {
      return api.get(`/api/households/${householdId}/budget-categories`);
    }
  },
  
  // ============ Akahu (NZ) API ============
  akahu: {
    async getStatus() {
      return api.get('/api/integrations/akahu/status');
    },
    
    async getConnection(householdId) {
      return api.get(`/api/integrations/akahu/households/${householdId}/connection`);
    },
    
    async getHealth(householdId) {
      return api.get(`/api/integrations/akahu/households/${householdId}/health`);
    },
    
    async getAccounts(householdId) {
      return api.get(`/api/integrations/akahu/households/${householdId}/accounts`);
    },
    
    async refreshAccounts(householdId) {
      return api.post(`/api/integrations/akahu/households/${householdId}/refresh-accounts`, {});
    },
    
    async syncTransactions(householdId, initial = false) {
      return api.post(`/api/integrations/akahu/households/${householdId}/sync?initial=${initial}`, {});
    },
    
    async devSync(householdId, days = 90) {
      return api.post(`/api/integrations/akahu/dev/sync/${householdId}?days=${days}`, {});
    },
    
    async disconnect(householdId) {
      return api.delete(`/api/integrations/akahu/households/${householdId}/disconnect`);
    },
    
    // OAuth connect URL - returns authorization URL for frontend
    async getConnectUrl(householdId) {
      return api.get(`/api/integrations/akahu/connect-url?household_id=${householdId}`);
    },
    
    // OAuth start - returns redirect URL (legacy)
    getStartUrl(householdId) {
      return `${API_URL}/api/integrations/akahu/start?household_id=${householdId}`;
    },
    
    // Dev Mode endpoints (Personal API tokens)
    dev: {
      async getAccounts() {
        return api.get('/api/integrations/akahu/dev/accounts');
      },
      
      async getTransactions(startDate = null, endDate = null, accountId = null) {
        const params = new URLSearchParams();
        if (startDate) params.append('start_date', startDate);
        if (endDate) params.append('end_date', endDate);
        if (accountId) params.append('account_id', accountId);
        const queryString = params.toString();
        return api.get(`/api/integrations/akahu/dev/transactions${queryString ? `?${queryString}` : ''}`);
      },
      
      async sync(householdId, days = 90) {
        return api.post(`/api/integrations/akahu/dev/sync/${householdId}?days=${days}`, {});
      },
      
      async getConnection(householdId) {
        return api.get(`/api/integrations/akahu/dev/connection/${householdId}`);
      }
    }
  },
  
  // ============== Weekly Close API ==============
  weeklyClose: {
    async start(weekStart, weekEnd) {
      return api.post('/api/weekly-close/start', { week_start: weekStart, week_end: weekEnd });
    },
    
    async compute(sessionId) {
      return api.post('/api/weekly-close/compute', { session_id: sessionId });
    },
    
    async finalize(sessionId, userChoice = null, reflection = null) {
      return api.post('/api/weekly-close/finalize', { 
        session_id: sessionId, 
        user_choice: userChoice,
        reflection
      });
    },
    
    async getLatest() {
      return api.get('/api/weekly-close/latest');
    },
    
    async getByWeek(weekStart) {
      return api.get(`/api/weekly-close/${weekStart}`);
    }
  },
  
  // ============== Budgets API ==============
  budgets: {
    async createTemplate(householdId, data) {
      return api.post(`/api/households/${householdId}/budgets/template`, data);
    },
    
    async getTemplates(householdId, activeOnly = true) {
      return api.get(`/api/households/${householdId}/budgets/templates?active_only=${activeOnly}`);
    },
    
    async createAdjustment(householdId, data) {
      return api.post(`/api/households/${householdId}/budgets/adjustments`, data);
    },
    
    async getAdjustments(householdId, weekStart) {
      return api.get(`/api/households/${householdId}/budgets/adjustments/${weekStart}`);
    }
  },
  
  // ============== Weekly Goals API ==============
  weeklyGoals: {
    async updateStatus(householdId, data) {
      return api.post(`/api/households/${householdId}/weekly-goals/update-status`, data);
    },
    
    async getByWeek(householdId, weekStart) {
      return api.get(`/api/households/${householdId}/weekly-goals/${weekStart}`);
    }
  },
  
  // ============== Calm Interventions API ==============
  interventions: {
    async get() {
      return api.get('/api/weekly-close/interventions');
    },
    
    async apply(optionId) {
      return api.post('/api/weekly-close/interventions/apply', { option_id: optionId });
    }
  },
  
  // ============== Partner & Household Permissions API ==============
  household: {
    // Partner invites
    async sendInvite(email, message = null) {
      return api.post('/api/household/invite', { email, message });
    },
    
    async getInvites(status = null, includeExpired = false) {
      const params = new URLSearchParams();
      if (status) params.append('status', status);
      if (includeExpired) params.append('include_expired', 'true');
      const query = params.toString();
      return api.get(`/api/household/invites${query ? `?${query}` : ''}`);
    },
    
    async acceptInvite(inviteId, displayName = null) {
      return api.post(`/api/household/invite/${inviteId}/accept`, { display_name: displayName });
    },
    
    async declineInvite(inviteId) {
      return api.post(`/api/household/invite/${inviteId}/decline`, {});
    },
    
    async cancelInvite(inviteId) {
      return api.delete(`/api/household/invite/${inviteId}`);
    },
    
    // Structural actions (confirmation workflow)
    async requestAction(actionType, payload, reason = null) {
      return api.post('/api/household/action-request', {
        action_type: actionType,
        payload,
        reason
      });
    },
    
    async getPendingActions() {
      return api.get('/api/household/pending-actions');
    },
    
    async approveAction(actionId, reason = null) {
      return api.post(`/api/household/action/${actionId}/approve`, { reason });
    },
    
    async declineAction(actionId, reason = null) {
      return api.post(`/api/household/action/${actionId}/decline`, { reason });
    },
    
    async cancelAction(actionId) {
      return api.delete(`/api/household/action/${actionId}`);
    },
    
    // Permission check
    async checkPermission(actionType) {
      return api.get(`/api/household/permission-check?action_type=${actionType}`);
    }
  },
  
  // Beta Feedback API (Module 10B)
  feedback: {
    // Get feature flags
    async getFlags() {
      return api.get('/api/feedback/flags');
    },
    
    // Submit text feedback
    async submitText({ emotion, pageKey, actionKey, text, consent }) {
      const formData = new FormData();
      formData.append('emotion', emotion);
      formData.append('page_key', pageKey);
      formData.append('action_key', actionKey);
      formData.append('input_mode', 'text');
      formData.append('consent', consent ? 'true' : 'false');
      formData.append('text', text);
      
      return api.post('/api/feedback/submit', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    },
    
    // Submit voice feedback
    async submitVoice({ emotion, pageKey, actionKey, audioBlob, consent }) {
      const formData = new FormData();
      formData.append('emotion', emotion);
      formData.append('page_key', pageKey);
      formData.append('action_key', actionKey);
      formData.append('input_mode', 'voice');
      formData.append('consent', consent ? 'true' : 'false');
      formData.append('audio_file', audioBlob, 'feedback.webm');
      
      return api.post('/api/feedback/submit', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    },
    
    // Get my feedback history
    async getHistory(limit = 20) {
      return api.get(`/api/feedback/me?limit=${limit}`);
    },
    
    // Admin: Get recent feedback (aggregated, no PII)
    async getAdminRecent(days = 7, limit = 50) {
      return api.get(`/api/feedback/admin/recent?days=${days}&limit=${limit}`);
    },
    
    // Admin: Get feedback trends summary
    async getAdminSummary(days = 30) {
      return api.get(`/api/feedback/admin/summary?days=${days}`);
    }
  }
};

export default api;
